import React, { useMemo, useState, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { observer } from 'mobx-react-lite';
import { FieldType, SortOrder } from 'choerodon-ui/pro/lib/data-set/enum';
import Icon from 'choerodon-ui/lib/icon';
import Popover from 'choerodon-ui/lib/popover';
import Tag from 'choerodon-ui/lib/tag';
import DataSet from '../../data-set/DataSet';
import Select from '../../select';
import SelectBox from '../../select-box';
import Button from '../../button';
import { FuncType, ButtonColor } from '../../button/interface';
import { $l } from '../../locale-context';
const { Option } = SelectBox;
const CombineSort = function CombineSort(props) {
    const { dataSet, prefixCls, sortableFieldNames } = props;
    const { fields, props: { combineSort, }, } = dataSet;
    const [visible, setVisible] = useState(false);
    const sortPrefixCls = `${prefixCls}-combine-sort`;
    const sortFieldOptions = useMemo(() => {
        const sortFieldData = [];
        if (fields && sortableFieldNames && sortableFieldNames.length > 0) {
            fields.forEach(field => {
                if (sortableFieldNames.includes(field.name)) {
                    sortFieldData.push({
                        value: field.name,
                        meaning: field.get('label') || field.name,
                    });
                }
            });
        }
        return new DataSet({
            autoQuery: false,
            autoCreate: false,
            paging: false,
            fields: [
                { name: 'value', type: FieldType.string },
                { name: 'meaning', type: FieldType.string },
            ],
            data: sortFieldData,
        });
    }, [fields, sortableFieldNames]);
    const sortDS = useMemo(() => {
        const { combineSortFieldNames = new Map() } = dataSet;
        const data = [];
        combineSortFieldNames.forEach((sortOrder, fieldName) => {
            const record = sortFieldOptions.records.find(record => record.get('value') === fieldName);
            if (record) {
                const field = dataSet.getField(fieldName);
                data.push({
                    sortName: fieldName,
                    order: sortOrder || (field && field.order) || SortOrder.asc,
                });
            }
        });
        return new DataSet({
            forceValidate: true,
            autoQuery: false,
            autoCreate: true,
            paging: false,
            fields: [
                { name: 'sortName', type: FieldType.string, options: sortFieldOptions },
                { name: 'order', type: FieldType.string, defaultValue: SortOrder.asc },
            ],
            data,
        });
    }, [sortFieldOptions, dataSet, dataSet.combineSortFieldNames, visible]);
    const optionsFilter = (record) => {
        return sortDS.data.every(sortRecord => sortRecord.get('sortName') !== record.get('value'));
    };
    const onVisibleChange = (visible) => {
        if (!visible) {
            sortDS.reset();
        }
        setVisible(visible);
    };
    const handleCancel = () => {
        sortDS.reset();
        setVisible(false);
    };
    const handleConfirm = () => {
        sortDS.validate().then(result => {
            if (result) {
                const records = sortDS.data.filter(r => r.get('sortName') && r.get('order'));
                const sortInfo = new Map();
                records.forEach(record => {
                    sortInfo.set(record.get('sortName'), record.get('order'));
                });
                dataSet.sort(sortInfo);
                setVisible(false);
            }
        });
    };
    const onDragEnd = useCallback((result) => {
        if (result.destination) {
            sortDS.move(result.source.index, result.destination.index);
        }
    }, [sortDS, sortDS.data]);
    const SortDragItem = ({ record, index }) => {
        const { key } = record;
        return (React.createElement(Draggable, { draggableId: String(key), index: record.index }, (pro, snapshot) => (React.createElement("span", Object.assign({ ref: pro.innerRef }, pro.draggableProps, { className: `${sortPrefixCls}-list-item${snapshot.isDragging ? ` ${sortPrefixCls}-list-item-dragging` : ''}` }),
            React.createElement("span", Object.assign({}, pro.dragHandleProps, { className: `${sortPrefixCls}-list-item-drag` }),
                React.createElement(Icon, { type: "baseline-drag_indicator" })),
            React.createElement("span", { className: `${sortPrefixCls}-list-item-index` },
                React.createElement(Tag, null, index + 1)),
            React.createElement(Select, { placeholder: $l('Table', 'please_select_column'), className: `${sortPrefixCls}-list-item-sortName`, record: record, name: "sortName", optionsFilter: optionsFilter, notFoundContent: $l('Table', 'no_save_filter'), clearButton: false }),
            React.createElement(SelectBox, { record: record, name: "order", className: `${sortPrefixCls}-list-item-sortOrder` },
                React.createElement(Option, { value: SortOrder.asc }, $l('Table', 'ascending')),
                React.createElement(Option, { value: SortOrder.desc }, $l('Table', 'descending'))),
            React.createElement(Button, { className: `${sortPrefixCls}-list-item-delete`, icon: 'delete_black-o', funcType: FuncType.link, color: ButtonColor.primary, onClick: () => sortDS.delete(record, false) })))));
    };
    const popupContent = useMemo(() => {
        return (React.createElement("div", { className: `${sortPrefixCls}-content` },
            React.createElement("div", { className: `${sortPrefixCls}-body` },
                React.createElement("div", { className: `${sortPrefixCls}-list-container` },
                    React.createElement(DragDropContext, { onDragEnd: onDragEnd },
                        React.createElement(Droppable, { droppableId: "combine-sort", direction: "vertical" }, (provided) => (React.createElement("div", Object.assign({ ref: provided.innerRef }, provided.droppableProps, { className: `${sortPrefixCls}-list` }),
                            sortDS.data.map((record, index) => {
                                const { key } = record;
                                return React.createElement(SortDragItem, { key: key, record: record, index: index });
                            }),
                            provided.placeholder))))),
                React.createElement("div", { className: `${sortPrefixCls}-add-button` },
                    React.createElement(Button, { funcType: FuncType.link, icon: "add", onClick: () => sortDS.create(), color: ButtonColor.primary, disabled: sortDS.data.length >= sortFieldOptions.records.length }, $l('Table', 'add_sort')))),
            React.createElement("div", { className: `${sortPrefixCls}-footer` },
                React.createElement(Button, { onClick: handleCancel }, $l('Modal', 'cancel')),
                React.createElement(Button, { onClick: handleConfirm, color: ButtonColor.primary }, $l('Modal', 'ok')))));
    }, [onDragEnd, sortFieldOptions, sortDS, sortDS.data]);
    if (!combineSort || !sortableFieldNames || sortableFieldNames.length === 0) {
        return null;
    }
    return (React.createElement(Popover, { trigger: "click", overlayClassName: `${sortPrefixCls}-popover`, title: $l('Table', 'custom_sort'), content: popupContent, visible: visible, onVisibleChange: onVisibleChange, placement: "bottomLeft" },
        React.createElement(Button, { icon: "paixu-xia", funcType: FuncType.link, color: ButtonColor.primary, className: `${sortPrefixCls}-trigger-button` })));
};
CombineSort.displayName = 'CombineSort';
export default observer(CombineSort);
